import { useEffect, useMemo, useState } from 'react';
import { TypedEventTarget } from 'typescript-event-target';

import { ApiError, axiosResponseToApiResponse } from 'container/models';
import { Auth } from 'types/auth';
import { ApplicationError } from 'types/errorHandling';

import { httpClient } from './apiReqSagaCreator/httpRequest';
import { ApiPaths } from './constants';
import { tenantCache } from './tenantCache';

const AUTH_TOKEN_LOCAL_STORAGE_ITEM = 'auth_token';
const EXCHANGE_TOKEN_LOCAL_STORAGE_ITEM = 'exchange_token';

const REFRESH_ERROR_SESSION_STORAGE_ITEM = 'last_token_refresh_error';

// we report the token as expired before it expires
const EXP_TIME_OFFSET_MS = -600000; // 10min

interface AuthCacheEvents {
  authTokenUpdated: CustomEvent<string>;
}

/*
If we ever add some kind of background fetching/revalidation, then we need to
create a means of one tab/process notifying other tabs/processes that it is
currently refreshing the token, and to wait until that's done. This would involve
faking the refreshingTokenPromise.
*/

class AuthCache extends TypedEventTarget<AuthCacheEvents> {
  get authToken(): string {
    return window.localStorage.getItem(AUTH_TOKEN_LOCAL_STORAGE_ITEM) ?? '';
  }
  private set authToken(value: string) {
    if (value === '')
      window.localStorage.removeItem(AUTH_TOKEN_LOCAL_STORAGE_ITEM);
    else window.localStorage.setItem(AUTH_TOKEN_LOCAL_STORAGE_ITEM, value);

    this.dispatchTypedEvent(
      'authTokenUpdated',
      new CustomEvent('authTokenUpdated', { detail: value })
    );
  }

  private authTokenExpiry?: { token: string; expiresAt: number };
  /**
   * `true` if expiration can't be parsed;
   * `false` if token is unset;
   * else reflects expiration status
   */
  get authTokenExpired(): boolean {
    // return false if no token because we can't refresh a token we don't have
    const token = this.authToken;
    if (!token) return false;

    if (!this.authTokenExpiry || this.authTokenExpiry.token !== token) {
      try {
        const exp =
          JSON.parse(atob(token.split('.')[1])).exp * 1000 + EXP_TIME_OFFSET_MS;
        this.authTokenExpiry = { token, expiresAt: exp };
      } catch (e) {
        const enhancedErr = ApplicationError.FromError(
          e,
          'failed to parse token timestamp'
        );
        enhancedErr.details.token = token;
        enhancedErr.print();

        this.authTokenExpiry = undefined;
        return true;
      }
    }

    return Date.now() > this.authTokenExpiry.expiresAt;
  }

  get exchangeToken(): string {
    return window.localStorage.getItem(EXCHANGE_TOKEN_LOCAL_STORAGE_ITEM) ?? '';
  }
  private set exchangeToken(value: string) {
    if (value === '')
      window.localStorage.removeItem(EXCHANGE_TOKEN_LOCAL_STORAGE_ITEM);
    else window.localStorage.setItem(EXCHANGE_TOKEN_LOCAL_STORAGE_ITEM, value);
  }

  get lastError(): string {
    return (
      window.sessionStorage.getItem(REFRESH_ERROR_SESSION_STORAGE_ITEM) ?? ''
    );
  }
  private set lastError(value: string) {
    if (value === '')
      window.sessionStorage.removeItem(REFRESH_ERROR_SESSION_STORAGE_ITEM);
    else
      window.sessionStorage.setItem(REFRESH_ERROR_SESSION_STORAGE_ITEM, value);
  }

  clear(): void {
    this.authToken = '';
    this.exchangeToken = '';
  }

  get loggedIn(): boolean {
    return this.authToken !== '';
  }

  private postLoginCommon(authResponse: Auth): Auth {
    this.authToken = authResponse.token.accessToken;
    this.exchangeToken = authResponse.token.refreshToken;

    this.lastError = '';

    tenantCache.initFromUser(authResponse.user);

    return authResponse;
  }

  login(username: string, password: string): Promise<Auth> {
    return axiosResponseToApiResponse(
      httpClient.post<Auth>(ApiPaths.auth.login(), {
        username,
        password,
      })
    ).then(this.postLoginCommon.bind(this), (error: ApiError) => {
      throw ApplicationError.FromError(error, 'Failed to log in');
    });
  }

  oidcLogin(url: string, code: string, state: string): Promise<Auth> {
    const oURL = new URL(url);
    oURL.searchParams.set('code', code);
    oURL.searchParams.set('state', state);

    return axiosResponseToApiResponse(
      httpClient.post<Auth>(oURL.toString())
    ).then(this.postLoginCommon.bind(this), (error: ApiError) => {
      throw ApplicationError.FromError(error, 'Failed to log in');
    });
  }

  oidcLink(url: string, code: string, state: string): Promise<Auth> {
    const oURL = new URL(url);
    oURL.searchParams.set('code', code);
    oURL.searchParams.set('state', state);

    return axiosResponseToApiResponse(
      httpClient.post<Auth>(oURL.toString(), undefined)
    ).then(this.postLoginCommon.bind(this), (error: ApiError) => {
      throw ApplicationError.FromError(error, 'Failed to link accounts');
    });
  }

  private refreshingToken: string = '';
  private refreshingTokenPromise: Promise<void> = Promise.resolve();
  refreshTokens(): Promise<void> {
    // if we don't have tokens, then abort with an empty promise
    if (this.exchangeToken === '' || this.authToken === '')
      return Promise.reject('unable to refresh tokens: missing tokens');

    // if we're already refreshing for the current exchange token, return the current promise to await on
    if (this.exchangeToken === this.refreshingToken)
      return this.refreshingTokenPromise;

    // otherwise: set the current exchange token as the one we're refreshing for...
    this.refreshingToken = this.exchangeToken;

    // and start the token refresh.
    this.refreshingTokenPromise = axiosResponseToApiResponse(
      httpClient.get<Tokens>(ApiPaths.auth.refreshToken(), {
        headers: {
          'Refresh-Token': this.exchangeToken,
        },
      })
    ).then(
      (response) => {
        this.authToken = response.accessToken;
        this.exchangeToken = response.refreshToken;

        this.lastError = '';
      },
      (error: ApiError) => {
        const appError = ApplicationError.FromError(
          error,
          'Failed to refresh user session'
        );

        this.lastError = appError.userMessage;

        throw appError;
      }
    );

    // return the refresh promise to await on.
    return this.refreshingTokenPromise;
  }

  get currentTokenRefresh(): Promise<void> {
    return this.refreshingTokenPromise;
  }

  logout(): Promise<void> {
    if (!this.loggedIn) return Promise.resolve();

    return axiosResponseToApiResponse(
      httpClient.get(ApiPaths.users.logOut._())
    ).finally(() => {
      this.clear();
      tenantCache.clear();
    });
  }
}

export const authCache = new AuthCache();

export const useAuthCache = (_authCache: AuthCache = authCache) => {
  const [currentAuthToken, _setCurrentAuthToken] = useState(
    _authCache.authToken
  );
  useEffect(() => {
    const listener = (newAuthToken: CustomEvent<string>): void => {
      _setCurrentAuthToken(newAuthToken.detail);
    };

    _authCache.addEventListener('authTokenUpdated', listener);

    return () => {
      _authCache.removeEventListener('authTokenUpdated', listener);
    };
  }, [_authCache]);

  const loggedIn = useMemo(() => currentAuthToken !== '', [currentAuthToken]);

  return {
    authToken: currentAuthToken,
    loggedIn,
  };
};
